/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef, useState, memo, useCallback } from "react";
import vegaEmbed from "vega-embed";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import {
  FunctionDeclaration,
  LiveServerToolCall,
  Type,
} from "@google/genai";
import { extractCoordinates, getLocationAndAddress } from "../../lib/geocoding";
import { AllPermissions } from "../../utils/permissions";

const altairDeclaration: FunctionDeclaration = {
  name: "render_altair",
  description: "Displays an altair graph in json format.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      json_graph: {
        type: Type.STRING,
        description:
          "JSON STRING representation of the graph to render. Must be a string, not a json object",
      },
    },
    required: ["json_graph"],
  },
};

const propertyEvaluationDeclaration: FunctionDeclaration = {
  name: "property_evaluation",
  description: "Evaluates a property and generates a PDF report with price prediction. Only call this after confirming all required data is collected.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      bathrooms: {
        type: Type.NUMBER,
        description: "Number of bathrooms in the property",
      },
      bedrooms: {
        type: Type.NUMBER,
        description: "Number of bedrooms in the property",
      },
      building_name_num: {
        type: Type.STRING,
        description: "Building name or number",
      },
      client_name: {
        type: Type.STRING,
        description: "Client name (default: 'Aqary')",
      },
      community: {
        type: Type.STRING,
        description: "Community or area name",
      },
      completion_status: {
        type: Type.STRING,
        description: "Property completion status (e.g., 'completed', 'under_construction')",
      },
      customer_name: {
        type: Type.STRING,
        description: "Customer name",
      },      emirate: {
        type: Type.STRING,
        description: "Emirate where the property is located. MUST be one of: 'Dubai', 'Fujairah', 'Ras Al Khaimah', 'Sharjah', 'Umm Al Quwain', 'Ajman', 'Abu Dhabi', 'Al Ain'",
      },
      furnished: {
        type: Type.STRING,
        description: "Whether the property is furnished (YES/NO)",
      },
      image_ids: {
        type: Type.ARRAY,
        description: "Array of image IDs (can be empty)",
        items: {
          type: Type.STRING,
        },
      },
      lat: {
        type: Type.NUMBER,
        description: "Latitude coordinate of the property",
      },
      long: {
        type: Type.NUMBER,
        description: "Longitude coordinate of the property",
      },      property_type: {
        type: Type.STRING,
        description: "Type of property. MUST be one of: 'apartment', 'villa', 'townhouse', 'penthouse', 'duplex', 'hotel & hotel apartment'",
      },
      size: {
        type: Type.NUMBER,
        description: "Property size in square meters",
      },
      street: {
        type: Type.STRING,
        description: "Street name",
      },
    },
    required: ["bathrooms", "bedrooms", "property_type", "size", "emirate", "community", "street", "customer_name", "building_name_num"],
  },
};

const dataConfirmationDeclaration: FunctionDeclaration = {
  name: "confirm_evaluation_data",
  description: "Confirms all collected data before proceeding with property evaluation API call. Validates property type is supported.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      ready_for_evaluation: {
        type: Type.BOOLEAN,
        description: "Whether all required data has been collected and confirmed",
      },
      collected_data: {
        type: Type.OBJECT,
        description: "Summary of all collected property data",
        properties: {
          bathrooms: { type: Type.NUMBER },
          bedrooms: { type: Type.NUMBER },
          property_type: { 
            type: Type.STRING,
            description: "MUST be one of: 'apartment', 'villa', 'townhouse', 'penthouse', 'duplex', 'hotel & hotel apartment'"
          },
          size: { type: Type.NUMBER },
          emirate: { 
            type: Type.STRING,
            description: "MUST be one of: 'Dubai', 'Fujairah', 'Ras Al Khaimah', 'Sharjah', 'Umm Al Quwain', 'Ajman', 'Abu Dhabi', 'Al Ain'"
          },
          community: { type: Type.STRING },
          furnished: { type: Type.STRING },
          building_name_num: { type: Type.STRING },
          customer_name: { type: Type.STRING },
          street: { type: Type.STRING },
        },
      },
    },
    required: ["ready_for_evaluation", "collected_data"],
  },
};

// Function to directly trigger evaluation when user says "proceed to evaluation"
const proceedToEvaluationDeclaration: FunctionDeclaration = {
  name: "proceed_to_evaluation",
  description: "Triggers property evaluation when user explicitly says 'proceed to evaluation', 'start evaluation', 'evaluate the property', or similar commands. Use this when the user wants to proceed with evaluation of confirmed data.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      proceed: {
        type: Type.BOOLEAN,
        description: "Always true when user wants to proceed with evaluation",
      },
    },
    required: ["proceed"],
  },
};

interface AltairComponentProps {
  permissions?: AllPermissions | null;
  userData?: {username: string; mobileNumber: string} | null;
}

// Simple Altair chart component using vega-embed
const Altair = ({ spec }: { spec: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current && spec) {
      try {
        const parsedSpec = JSON.parse(spec);
        vegaEmbed(containerRef.current, parsedSpec, { actions: false });
      } catch (error) {
        console.error("Error rendering Altair chart:", error);
      }
    }
  }, [spec]);

  return <div ref={containerRef} className="w-full" />;
};

// Evaluation Results component with improved styling
const EvaluationResults = ({ result, confirmedData, onClose }: any) => {
  return (
    <div style={{
      backgroundColor: 'var(--Neutral-00, #ffffff)',
      borderRadius: '12px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      padding: '24px',
      marginTop: '16px',
      marginLeft: '8px',
      marginRight: '8px',
      border: '1px solid var(--Neutral-15, #e5e7eb)',
      maxWidth: '800px',
      margin: '16px auto'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        borderBottom: '2px solid var(--Blue-500, #3b82f6)',
        paddingBottom: '12px'
      }}>
        <h3 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'var(--text, #1a1a1a)',
          margin: 0
        }}>
          🏠 Property Evaluation Results
        </h3>
        <button 
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: 'var(--Neutral-50, #6b7280)',
            padding: '4px 8px',
            borderRadius: '4px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--Neutral-10, #f3f4f6)';
            e.currentTarget.style.color = 'var(--Neutral-80, #374151)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--Neutral-50, #6b7280)';
          }}
        >
          ✕
        </button>
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Predicted Price */}
        <div style={{
          backgroundColor: 'var(--Blue-50, #eff6ff)',
          padding: '20px',
          borderRadius: '8px',
          border: '2px solid var(--Blue-500, #3b82f6)'
        }}>
          <p style={{
            fontSize: '36px',
            fontWeight: 'bold',
            color: 'var(--Blue-800, #1e40af)',
            margin: '0 0 8px 0'
          }}>
            💰 {result.predicted_price?.toLocaleString()} {result.currency || 'AED'}
          </p>
          <p style={{
            fontSize: '14px',
            color: 'var(--Neutral-60, #4b5563)',
            margin: 0,
            fontWeight: '500'
          }}>
            Predicted Property Price
          </p>
        </div>

        {/* Price Range */}
        {result.price_range && (
          <div style={{
            backgroundColor: 'var(--Neutral-5, #f9fafb)',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid var(--Neutral-15, #e5e7eb)'
          }}>
            <p style={{
              fontSize: '20px',
              fontWeight: '600',
              color: 'var(--text, #1a1a1a)',
              margin: '0 0 4px 0'
            }}>
              📊 {result.price_range}
            </p>
            <p style={{
              fontSize: '14px',
              color: 'var(--Neutral-60, #4b5563)',
              margin: 0
            }}>
              Estimated Price Range
            </p>
          </div>
        )}

        {/* Property Details Summary */}
        {confirmedData && (
          <div style={{
            backgroundColor: 'var(--Neutral-5, #f9fafb)',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid var(--Neutral-15, #e5e7eb)'
          }}>
            <p style={{
              fontSize: '16px',
              fontWeight: '600',
              color: 'var(--text, #1a1a1a)',
              marginBottom: '12px'
            }}>
              📋 Property Details
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '12px',
              fontSize: '14px',
              color: 'var(--Neutral-80, #374151)'
            }}>
              <div><strong>Type:</strong> {confirmedData.property_type}</div>
              <div><strong>Bedrooms:</strong> {confirmedData.bedrooms}</div>
              <div><strong>Bathrooms:</strong> {confirmedData.bathrooms}</div>
              <div><strong>Size:</strong> {confirmedData.size} sqm</div>
              <div><strong>Location:</strong> {confirmedData.community}, {confirmedData.emirate}</div>
              <div><strong>Furnished:</strong> {confirmedData.furnished}</div>
            </div>
          </div>
        )}

        {/* PDF Download Button */}
        {result.pdf_download_link && (
          <a
            href={result.pdf_download_link}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              padding: '14px 28px',
              backgroundColor: 'var(--Blue-500, #3b82f6)',
              color: '#ffffff',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: '16px',
              textAlign: 'center',
              transition: 'all 0.2s',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--Blue-800, #1e40af)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--Blue-500, #3b82f6)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
            }}
          >
            📄 Download PDF Report
          </a>
        )}

        {/* Expiry Notice */}
        {result.pdf_download_link && (
          <p style={{
            fontSize: '12px',
            color: 'var(--Neutral-50, #6b7280)',
            fontStyle: 'italic',
            margin: '8px 0 0 0',
            textAlign: 'center'
          }}>
            ⏰ Download link expires in 72 hours
          </p>
        )}

        {/* Error Display */}
        {result.error && (
          <div style={{
            backgroundColor: 'var(--Red-50, #fef2f2)',
            padding: '16px',
            borderRadius: '8px',
            border: '2px solid var(--Red-500, #ef4444)'
          }}>
            <p style={{
              fontSize: '16px',
              fontWeight: '600',
              color: 'var(--Red-700, #b91c1c)',
              margin: 0
            }}>
              ⚠️ Error: {result.error}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

function AltairComponent({ permissions, userData }: AltairComponentProps) {
  const [jsonString, setJSONString] = useState<string>("");
  const [evaluationResult, setEvaluationResult] = useState<any>(null);
  const [confirmedData, setConfirmedData] = useState<any>(null);
  const [autoLocationData, setAutoLocationData] = useState<any>(null);
  const [showEvaluationButton, setShowEvaluationButton] = useState<boolean>(false);
  const [showTestBanner, setShowTestBanner] = useState<boolean>(true);
  const [isEvaluating, setIsEvaluating] = useState<boolean>(false);
  const { client, connected } = useLiveAPIContext();
  
  // CRITICAL FIX: Use refs to avoid event listener churn
  const confirmedDataRef = useRef<any>(null);
  const evaluationResultRef = useRef<any>(null);

  // DEBUG: Log component mount and client changes
  useEffect(() => {
    console.log(`🔍 [ALTAIR] Component mounted/updated`);
    console.log(`🔍 [ALTAIR] Current client ID: ${client?.clientId}`);
    console.log(`🔍 [ALTAIR] Connected state: ${connected}`);
  }, [client, connected]);

  // DEBUG: Log state changes
  useEffect(() => {
    console.log(`📊 [ALTAIR] State changed - confirmedData:`, confirmedData);
    if (confirmedData) {
      console.log(`📊 [ALTAIR] confirmedData fields:`, Object.keys(confirmedData));
      console.log(`📊 [ALTAIR] confirmedData has data: ${Object.keys(confirmedData).length > 0}`);
    }
  }, [confirmedData]);

  useEffect(() => {
    console.log(`📊 [ALTAIR] State changed - showEvaluationButton: ${showEvaluationButton}`);
  }, [showEvaluationButton]);

  useEffect(() => {
    console.log(`📊 [ALTAIR] State changed - evaluationResult:`, evaluationResult);
  }, [evaluationResult]);
  
  // CRITICAL FIX: Keep refs in sync with state
  useEffect(() => {
    confirmedDataRef.current = confirmedData;
    evaluationResultRef.current = evaluationResult;
    console.log(`🔄 [ALTAIR] Refs updated - confirmedData:`, confirmedData ? 'present' : 'null');
  }, [confirmedData, evaluationResult]);

  // TEST MODE: Load sample data when URL parameter is present
  // Access with: http://localhost:3000?testEvaluation=true (complete data)
  // Or: http://localhost:3000?testPartialData=true (incomplete data - missing info stage)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const testMode = urlParams.get('testEvaluation') === 'true';
    const testPartialMode = urlParams.get('testPartialData') === 'true';
    
    // Full data test mode (ready for evaluation)
    if (testMode) {
      console.log("🧪 [TEST MODE] Loading complete sample property data from URL parameter");
      console.log("🧪 [TEST MODE] This bypasses the full conversation flow for quick testing");
      
      const sampleData = {
        bathrooms: 2,
        bedrooms: 3,
        property_type: "apartment",
        size: 1500,
        emirate: "Dubai",
        community: "Dubai Marina",
        furnished: "YES",
        building_name_num: "Marina Tower 1",
        customer_name: "Test Customer",
        street: "Marina Walk"
      };
      
      setConfirmedData(sampleData);
      setShowEvaluationButton(true);
      
      // Set mock location data for Dubai Marina
      setAutoLocationData({
        coordinates: {
          lat: 25.0772,
          long: 55.1396
        },
        address: {
          emirate: "Dubai",
          community: "Dubai Marina",
          street: "Marina Walk",
          formatted_address: "Marina Walk, Dubai Marina, Dubai"
        }
      });
      
      console.log("✅ [TEST MODE] Complete sample data loaded. Click 'Proceed to Evaluation' button to test API");
    }
    
    // Partial data test mode (AI collecting missing info stage)
    if (testPartialMode) {
      console.log("🧪 [PARTIAL TEST MODE] Loading incomplete property data");
      console.log("🧪 [PARTIAL TEST MODE] Simulating state after camera analysis, missing user-provided info");
      
      const partialData = {
        // ✅ Data collected from camera/visual analysis
        bathrooms: 2,
        bedrooms: 3,
        property_type: "apartment",
        size: 1500,
        furnished: "YES",
        
        // ❌ Missing data that AI needs to ask user for
        emirate: "",  // Missing - AI will ask
        community: "",  // Missing - AI will ask
        building_name_num: "",  // Missing - AI will ask
        customer_name: "",  // Missing - AI will ask
        street: ""  // Missing - AI will ask
      };
      
      setConfirmedData(partialData);
      setShowEvaluationButton(false);  // Don't show button until data is complete
      
      console.log("✅ [PARTIAL TEST MODE] Partial data loaded with missing fields:");
      console.log("   - Missing: emirate, community, building_name_num, customer_name, street");
      console.log("   - Waiting for connection to send context to AI");
    }
  }, [client]);

  // Send context to AI when connected in partial test mode
  // Inject context via tool response to inform AI about pre-collected data
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const testPartialMode = urlParams.get('testPartialData') === 'true';
    
    if (connected && testPartialMode && confirmedData && client) {
      console.log("🔌 [PARTIAL TEST MODE] Connection established, injecting context");
      
      setTimeout(() => {
        // Send a "system context" via tool response
        client.sendToolResponse({
          functionResponses: [{
            response: { 
              output: { 
                context: "visual_tour_completed",
                collected_data: {
                  bedrooms: confirmedData.bedrooms,
                  bathrooms: confirmedData.bathrooms,
                  property_type: confirmedData.property_type,
                  size: confirmedData.size,
                  furnished: confirmedData.furnished
                },
                missing_data: ["emirate", "community", "street", "building_name_num", "customer_name"],
                instruction: "Visual tour complete. You have collected basic property information from the camera. Now greet the user briefly and immediately start asking for the missing location and customer information. Start with: 'Which emirate is this property located in?'"
              }
            },
            id: "partial_context_" + Date.now(),
            name: "system_context_injection",
          }]
        });
        console.log("✅ [PARTIAL TEST MODE] Context injected via tool response");
        console.log("🤖 [PARTIAL TEST MODE] AI should now skip full greeting and ask for emirate");
      }, 1500);
    }
  }, [connected, confirmedData, client]);

  // Handle evaluation success
  const handleEvaluationSuccess = useCallback(async (result: any) => {
    console.log("📤 [GEMINI] Sending evaluation success message to Gemini");
    setTimeout(() => {
      client.sendToolResponse({
        functionResponses: [{
          response: { 
            output: { 
              success: true, 
              evaluation_completed: true,
              predicted_price: result.predicted_price,
              currency: result.currency || 'AED',
              price_range: result.price_range,
              pdf_available: !!result.pdf_download_link,
              message: "Evaluation completed successfully"
            }
          },
          id: "evaluation_success_" + Date.now(),
          name: "evaluation_completed",
        }]
      });
    }, 100);
  }, [client]);

  // Handle close results
  const handleCloseResults = useCallback(() => {
    console.log("🔄 [GEMINI] Closing evaluation results");
    setEvaluationResult(null);
    setShowEvaluationButton(false); // Hide the button when closing results
    // Don't clear confirmedData to allow re-evaluation if needed
    
    setTimeout(() => {
      client.sendToolResponse({
        functionResponses: [{
          response: { 
            output: { 
              closed: true,
              message: "Results closed by user"
            }
          },
          id: "close_results_" + Date.now(),
          name: "close_evaluation_results",
        }]
      });
    }, 100);
  }, [client]);

  // Property evaluation function for the proceed button
  const handleProceedToEvaluation = useCallback(async () => {
    console.log(`🎬 [API_CALL] ========================================`);
    console.log(`🎬 [API_CALL] handleProceedToEvaluation() STARTED`);
    
    // ✅ Use ref to get latest confirmedData value (not closure)
    const currentData = confirmedDataRef.current;
    
    console.log(`🔍 [API_CALL] Checking confirmedData from ref:`, currentData);
    
    if (!currentData) {
      console.warn("⚠️ [API_CALL] No confirmed data available for evaluation - ABORTING");
      console.log(`🎬 [API_CALL] ========================================`);
      return;
    }
    
    console.log(`✅ [API_CALL] Data available - proceeding with evaluation`);
    setIsEvaluating(true); // Show loading state
    console.log(`🔄 [API_CALL] Set isEvaluating = true`);

    // Validate and normalize furnished value - API expects 'YES', 'NO', or 'PARTLY'
    const validFurnishedValues = ['YES', 'NO', 'PARTLY'];
    let furnishedValue = String(currentData.furnished || 'NO').toUpperCase().trim();
    if (!validFurnishedValues.includes(furnishedValue)) {
      // Map common variations to valid values
      if (furnishedValue === 'FURNISHED' || furnishedValue === 'TRUE') {
        furnishedValue = 'YES';
      } else if (furnishedValue === 'UNFURNISHED' || furnishedValue === 'FALSE') {
        furnishedValue = 'NO';
      } else if (furnishedValue === 'SEMI-FURNISHED' || furnishedValue === 'SEMI' || furnishedValue === 'PARTIAL') {
        furnishedValue = 'PARTLY';
      } else {
        // Default to 'NO' if still invalid
        console.warn(`⚠️ [API_CALL] Invalid furnished value "${currentData.furnished}", defaulting to "NO"`);
        furnishedValue = 'NO';
      }
    }
    console.log(`✅ [API_CALL] Furnished value normalized: ${currentData.furnished} → ${furnishedValue}`);

    // Prepare property data from confirmed data
    const propertyData = {
        bathrooms: currentData.bathrooms || 1,
        bedrooms: currentData.bedrooms || 1,
        building_name_num: currentData.building_name_num || "",
        client_name: currentData.client_name || "Aqary",
        community: currentData.community || "",
        completion_status: currentData.completion_status || "completed",
        customer_name: currentData.customer_name || "",
        emirate: currentData.emirate || "Dubai",
        furnished: furnishedValue,  // Use validated value
        image_ids: currentData.image_ids || [],
        lat: autoLocationData?.coordinates?.lat || 25.2048,
        long: autoLocationData?.coordinates?.long || 55.2708,
        property_type: (currentData.property_type || "apartment").toLowerCase().trim(),
        size: currentData.size || 0,
        street: currentData.street || "",
      };

    console.log(`📦 [API_CALL] Prepared property data for API:`, propertyData);
    console.log(`🌐 [API_CALL] Making POST request to: https://valuation.aqary.ai/predict/price`);

    try {
      const response = await fetch('https://valuation.aqary.ai/predict/price', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(propertyData),
      });

      console.log(`📥 [API_CALL] Response received - Status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        // Try to get error details from response body
        let errorDetails = '';
        try {
          const errorBody = await response.text();
          errorDetails = errorBody ? ` - ${errorBody}` : '';
          console.error(`❌ [API_CALL] Error response body:`, errorBody);
        } catch (e) {
          console.error(`❌ [API_CALL] Could not read error response body`);
        }
        throw new Error(`API call failed with status: ${response.status}${errorDetails}`);
      }

      const result = await response.json();
      console.log(`✅ [API_CALL] API response parsed successfully:`, result);
      
      // Preserve the property details in the evaluation result
      const resultWithDetails = {
        ...result,
        property_details: {
          ...propertyData,
          // Add formatted display values
          bedrooms: propertyData.bedrooms,
          bathrooms: propertyData.bathrooms,
          property_type: propertyData.property_type,
          size: propertyData.size,
          emirate: propertyData.emirate,
          community: propertyData.community,
          building_name_num: propertyData.building_name_num,
          street: propertyData.street,
          customer_name: propertyData.customer_name,
          furnished: propertyData.furnished
        }
      };
      
      console.log(`💾 [API_CALL] Setting evaluation result in state...`);
      setEvaluationResult(resultWithDetails);
      setShowEvaluationButton(false); // Hide button after successful evaluation
      
      console.log(`✅ [API_CALL] Evaluation completed successfully!`);
      console.log(`✅ [API_CALL] - Predicted price: ${result.predicted_price} ${result.currency || 'AED'}`);
      console.log(`✅ [API_CALL] - PDF available: ${!!result.pdf_download_link}`);
      console.log(`✅ [API_CALL] - Results panel will automatically appear in UI`);
      console.log(`🎬 [API_CALL] ========================================`);
      // Results panel will automatically appear - no need to notify AI
      
    } catch (error) {
      console.error('❌ [API_CALL] ========================================');
      console.error('❌ [API_CALL] Property evaluation FAILED:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('❌ [API_CALL] Error details:', errorMessage);
      
      const errorResult = { 
        error: 'Failed to evaluate property: ' + errorMessage
      };
      console.error('❌ [API_CALL] Setting error result in state:', errorResult);
      setEvaluationResult(errorResult);
      setShowEvaluationButton(true); // Keep button visible on error to allow retry
      console.error('❌ [API_CALL] ========================================');
    } finally {
      console.log(`🏁 [API_CALL] Finally block - clearing loading state`);
      setIsEvaluating(false); // Clear loading state
      console.log(`🏁 [API_CALL] isEvaluating set to false - handleProceedToEvaluation() COMPLETE`);
    }
  }, [confirmedData, autoLocationData, client]);


  // Note: Location detection is now handled manually via button click
  // This function is kept for future use if needed
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const attemptLocationDetection = async () => {
    try {
      // Check if location permission was granted during welcome flow
      if (permissions && !permissions.location.granted) {
        console.warn("Location permission was not granted during welcome flow");
        setAutoLocationData(null);
        return null;
      }

      // Attempting automatic location detection
      const locationData = await getLocationAndAddress();
      setAutoLocationData(locationData);
      // Auto-location detection successful
      return locationData;
    } catch (error) {
      // Auto-location detection failed
      setAutoLocationData(null);
      return null;
    }
  };

  // Note: Removed auto-attempt location detection
  // Location detection now only happens when user manually clicks "Locate me" button
  // useEffect(() => {
  //   // Model selection is now handled by the backend ephemeral token
  //   // All configuration (system instructions, tools, voice, response modalities)
  //   // is locked in the backend token for security
  //   setModel("models/gemini-live-2.5-flash-preview");
  //   setConfig({
  //     // Empty config - everything is locked in the backend ephemeral token
  //     // This includes: systemInstruction, tools, responseModalities, speechConfig
  //   });
  // }, [setConfig, setModel]);

  useEffect(() => {
    console.log(`🔔 [EVENT_LISTENER] ========================================`);
    console.log(`🔔 [EVENT_LISTENER] Setting up tool call listener for client: ${client?.clientId}`);
    console.log(`🔔 [EVENT_LISTENER] Current confirmedData at subscription time:`, confirmedDataRef.current);
    console.log(`🔔 [EVENT_LISTENER] ========================================`);
    
    const onToolCall = async (toolCall: LiveServerToolCall) => {
      console.log(`🔧 [TOOL_CALL] ========================================`);
      console.log(`🔧 [TOOL_CALL] [${client?.clientId}] Tool call RECEIVED in Altair component`);
      console.log(`🔧 [TOOL_CALL] [${client?.clientId}] Full toolCall object:`, toolCall);
      console.log(`🔧 [TOOL_CALL] [${client?.clientId}] Function names:`, toolCall.functionCalls?.map(fc => fc.name));
      console.log(`🔧 [TOOL_CALL] [${client?.clientId}] ========================================`);
      
      // CRITICAL FIX: Use refs to get latest state without re-registering listeners
      const currentConfirmedData = confirmedDataRef.current;
      const currentEvalResult = evaluationResultRef.current;
      console.log(`📊 [TOOL_CALL] [${client?.clientId}] Current state from refs:`);
      console.log(`📊 [TOOL_CALL] [${client?.clientId}] - confirmedData:`, currentConfirmedData ? 'present' : 'null');
      console.log(`📊 [TOOL_CALL] [${client?.clientId}] - evaluationResult:`, currentEvalResult ? 'present' : 'null');
      
      if (!toolCall.functionCalls) {
        console.log(`⚠️ [ALTAIR] [${client?.clientId}] No function calls in tool call`);
        return;
      }

      // Handle altair rendering
      const altairFc = toolCall.functionCalls.find(
        (fc) => fc.name === altairDeclaration.name
      );
      if (altairFc) {
        console.log("📊 [GEMINI] Altair chart rendering requested by Gemini");
        const str = (altairFc.args as any).json_graph;
        setJSONString(str);
      }

      // Handle test echo
      const testEchoFc = toolCall.functionCalls.find(
        (fc) => fc.name === "test_echo"
      );
      if (testEchoFc) {
        console.log(`🔔 [ALTAIR] [${client?.clientId}] test_echo tool called!`);
        console.log(`🔔 [ALTAIR] [${client?.clientId}] Args:`, testEchoFc.args);
        const message = (testEchoFc.args as any).message;
        
        // Log to console for verification
        console.log(`✅ [ALTAIR] [${client?.clientId}] Tool calling works! Message: ${message}`);
        
        // Send response back to AI
        client.sendToolResponse({
          functionResponses: [{
            response: { 
              output: { 
                echoed_message: message,
                success: true,
                timestamp: new Date().toISOString()
              }
            },
            id: testEchoFc.id,
            name: testEchoFc.name,
          }]
        });
        
        console.log("✅ [TEST] Tool response sent back to AI");
      }

      // Handle data confirmation
      const confirmationFc = toolCall.functionCalls.find(
        (fc) => fc.name === dataConfirmationDeclaration.name
      );
      if (confirmationFc) {
        console.log(`✅ [ALTAIR] [${client?.clientId}] Data confirmation tool call received!`);
        console.log(`✅ [ALTAIR] [${client?.clientId}] Full args:`, confirmationFc.args);
        const confirmedData = (confirmationFc.args as any).collected_data;
        console.log(`✅ [ALTAIR] [${client?.clientId}] Collected data:`, confirmedData);
        
        // Validate property type in confirmed data
        const supportedPropertyTypes = [
          "apartment", "villa", "townhouse", "penthouse", "duplex", "hotel & hotel apartment"
        ];
        
        const supportedEmirates = [
          "Dubai", "Fujairah", "Ras Al Khaimah", "Sharjah", "Umm Al Quwain", "Ajman", "Abu Dhabi", "Al Ain"
        ];
        
        if (confirmedData.property_type && !supportedPropertyTypes.includes(confirmedData.property_type)) {
          console.warn(`Unsupported property type in confirmation: ${confirmedData.property_type}`);
          // Add a warning flag to the confirmed data
          confirmedData.property_type_warning = `Property type "${confirmedData.property_type}" is not supported. Please choose from: ${supportedPropertyTypes.join(", ")}`;
        }
        
        if (confirmedData.emirate && !supportedEmirates.includes(confirmedData.emirate)) {
          console.warn(`Unsupported emirate in confirmation: ${confirmedData.emirate}`);
          // Add a warning flag to the confirmed data
          confirmedData.emirate_warning = `Emirate "${confirmedData.emirate}" is not valid. Please choose from: ${supportedEmirates.join(", ")}`;
        }
        
        // Validate all required fields at confirmation stage
        const requiredFields = ['bathrooms', 'bedrooms', 'property_type', 'size', 'emirate', 'community', 'street', 'customer_name', 'building_name_num'];
        const missingFields = [];
        
        for (const field of requiredFields) {
          const value = confirmedData[field];
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            missingFields.push(field);
          }
        }
        
        if (missingFields.length > 0) {
          console.warn(`Missing required fields in confirmation: ${missingFields.join(', ')}`);
          confirmedData.validation_warning = `Missing required fields: ${missingFields.join(', ')}. Please provide these before proceeding with evaluation.`;
        }
        
        console.log(`📝 [ALTAIR] [${client?.clientId}] Calling setConfirmedData with:`, confirmedData);
        setConfirmedData(confirmedData);
        console.log(`✅ [ALTAIR] [${client?.clientId}] setConfirmedData called - UI should update!`);
        
        if (missingFields.length > 0) {
          console.warn(`⚠️ [ALTAIR] [${client?.clientId}] VALIDATION FAILED: Missing required fields`, missingFields);
        }
        
        // IMPROVED TOOL RESPONSE: Be explicit about what happened
        console.log(`📤 [TOOL_FLOW] [${client?.clientId}] ========================================`);
        console.log(`📤 [TOOL_FLOW] [${client?.clientId}] STEP 1: Preparing tool response for confirm_evaluation_data`);
        console.log(`📤 [TOOL_FLOW] [${client?.clientId}] - Tool ID: ${confirmationFc.id}`);
        console.log(`📤 [TOOL_FLOW] [${client?.clientId}] - Status: ${missingFields.length > 0 ? "partial" : "confirmed"}`);
        console.log(`📤 [TOOL_FLOW] [${client?.clientId}] - Missing fields: ${missingFields.length > 0 ? missingFields.join(', ') : 'none'}`);
        
        const confirmToolResponse = {
          functionResponses: [{
            response: { 
              output: { 
                status: missingFields.length > 0 ? "partial" : "confirmed",
                action_taken: "displayed_ui_panel",
                next_step: missingFields.length > 0 ? "collect_missing_data" : "wait_for_user_to_say_proceed",
                ui_visible: true,
                missing_fields: missingFields,
                has_warnings: (confirmedData.property_type_warning || confirmedData.emirate_warning || confirmedData.validation_warning) ? true : false,
                data_summary: {
                  bedrooms: confirmedData.bedrooms,
                  bathrooms: confirmedData.bathrooms,
                  property_type: confirmedData.property_type,
                  emirate: confirmedData.emirate,
                  community: confirmedData.community
                },
                message: missingFields.length > 0 
                  ? `Data received but missing required fields: ${missingFields.join(', ')}`
                  : "All data confirmed and displayed in UI. User can now say 'proceed' to start evaluation."
              }
            },
            id: confirmationFc.id,
            name: confirmationFc.name,
          }]
        };
        
        console.log(`📤 [TOOL_FLOW] [${client?.clientId}] STEP 2: Tool response payload:`, JSON.stringify(confirmToolResponse, null, 2));
        console.log(`📤 [TOOL_FLOW] [${client?.clientId}] STEP 3: Sending tool response to AI...`);
        
        client.sendToolResponse(confirmToolResponse);
        
        console.log(`✅ [TOOL_FLOW] [${client?.clientId}] STEP 4: Tool response sent successfully!`);
        console.log(`✅ [TOOL_FLOW] [${client?.clientId}] - AI should now receive the response and speak`);
        console.log(`✅ [TOOL_FLOW] [${client?.clientId}] ========================================`);
      }

      // Handle property evaluation
      const propertyFc = toolCall.functionCalls.find(
        (fc) => fc.name === propertyEvaluationDeclaration.name
      );
      
      let evalResult: any = null;
      
      if (propertyFc) {
        console.log("🏠 [GEMINI] Property evaluation requested by Gemini");
        try {
          const supportedPropertyTypes = [
            "apartment", "villa", "townhouse", "penthouse", "duplex", "hotel & hotel apartment"
          ];
          
          const supportedEmirates = [
            "Dubai", "Fujairah", "Ras Al Khaimah", "Sharjah", "Umm Al Quwain", "Ajman", "Abu Dhabi", "Al Ain"
          ];
          
          const submittedPropertyType = (propertyFc.args as any).property_type;
          const submittedEmirate = (propertyFc.args as any).emirate;
            // Update confirmed data with the final values used for evaluation
          // Validate required fields first
          const submittedStreet = (propertyFc.args as any).street;
          const submittedCustomerName = (propertyFc.args as any).customer_name;
          const submittedBuildingName = (propertyFc.args as any).building_name_num;
          const submittedCommunity = (propertyFc.args as any).community;
          
          const finalConfirmedData: any = {
            bathrooms: (propertyFc.args as any).bathrooms || 1,
            bedrooms: (propertyFc.args as any).bedrooms || 1,
            property_type: submittedPropertyType,
            size: (propertyFc.args as any).size || 0,
            emirate: submittedEmirate,
            community: submittedCommunity,
            furnished: (propertyFc.args as any).furnished ?? "NO",
            building_name_num: submittedBuildingName,
            customer_name: submittedCustomerName,
            street: submittedStreet,
          };
          
          // Validate and add warnings if needed
          if (!supportedPropertyTypes.includes(submittedPropertyType)) {
            finalConfirmedData.property_type_warning = `Property type "${submittedPropertyType}" is not supported. Please choose from: ${supportedPropertyTypes.join(", ")}`;
          }
          
          if (!supportedEmirates.includes(submittedEmirate)) {
            finalConfirmedData.emirate_warning = `Emirate "${submittedEmirate}" is not valid. Please choose from: ${supportedEmirates.join(", ")}`;
          }
          
          // Update the confirmed data display with final values
          setConfirmedData(finalConfirmedData);
          
          // Validate property type
          if (!supportedPropertyTypes.includes(submittedPropertyType)) {
            throw new Error(`Unsupported property type: "${submittedPropertyType}". Supported types are: ${supportedPropertyTypes.join(", ")}`);
          }
          
          // Validate emirate
          if (!supportedEmirates.includes(submittedEmirate)) {
            throw new Error(`Unsupported emirate: "${submittedEmirate}". Supported emirates are: ${supportedEmirates.join(", ")}`);
          }

          // Validate required fields are not empty
          
          if (!submittedStreet || submittedStreet.trim() === "") {
            throw new Error(`Street name is required for property evaluation. Please provide the street name or address.`);
          }
          
          if (!submittedCustomerName || submittedCustomerName.trim() === "") {
            throw new Error(`Customer name is required for property evaluation. Please provide your name.`);
          }
          
          if (!submittedBuildingName || submittedBuildingName.trim() === "") {
            throw new Error(`Building name/number is required for property evaluation. Please provide the building name or number.`);
          }
          
          if (!submittedCommunity || submittedCommunity.trim() === "") {
            throw new Error(`Community name is required for property evaluation. Please provide the community name.`);
          }
          
          // Extract coordinates from address information
          let coordinates = null;
          
          // Try to extract coordinates from the provided address information
          if (submittedEmirate && submittedCommunity) {
            coordinates = await extractCoordinates({
              emirate: submittedEmirate,
              community: submittedCommunity,
              street: submittedStreet,
              building_name_num: submittedBuildingName
            });
          }

          // Using final coordinates for property evaluation

          const propertyData = {
            bathrooms: (propertyFc.args as any).bathrooms || 1,
            bedrooms: (propertyFc.args as any).bedrooms || 1,
            building_name_num: submittedBuildingName,
            community: submittedCommunity,
            customer_name: submittedCustomerName,
            emirate: submittedEmirate,
            furnished: (propertyFc.args as any).furnished ?? "NO",
            latitude: coordinates?.lat || 0,
            longitude: coordinates?.long || 0,
            property_type: submittedPropertyType,
            size: (propertyFc.args as any).size || 0,
            street: submittedStreet,
          };

          // Real API call with actual property data
          console.log("🔄 [GEMINI] Making real API call with property data:", propertyData);
          
          const response = await fetch('https://valuation.aqary.ai/predict/price', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(propertyData),
          });

          if (!response.ok) {
            throw new Error(`Property evaluation API failed: ${response.statusText}`);
          }

          const result = await response.json();
          console.log("✅ [GEMINI] Property evaluation successful:", result);
          
          evalResult = result;
          
          setEvaluationResult(result);
          
          // Show success message via tool response
          await handleEvaluationSuccess(result);
          
        } catch (error) {
          console.error("❌ [GEMINI] Property evaluation failed:", error);
          evalResult = null;
          setEvaluationResult(null);
          throw error;
        }
      }

      // Handle proceed to evaluation button click
      const proceedFc = toolCall.functionCalls.find(
        (fc) => fc.name === proceedToEvaluationDeclaration.name
      );
      
      if (proceedFc) {
        console.log(`🚀 [TOOL_FLOW] [${client?.clientId}] ========================================`);
        console.log(`🚀 [TOOL_FLOW] [${client?.clientId}] proceed_to_evaluation tool call RECEIVED`);
        console.log(`🚀 [TOOL_FLOW] [${client?.clientId}] - Tool ID: ${proceedFc.id}`);
        console.log(`🚀 [TOOL_FLOW] [${client?.clientId}] - Args:`, proceedFc.args);
        console.log(`🚀 [TOOL_FLOW] [${client?.clientId}] - Current confirmedData from ref:`, currentConfirmedData);
        
        // Directly proceed with evaluation instead of showing button
        if (currentConfirmedData && Object.keys(currentConfirmedData).length > 0) {
          console.log(`✅ [TOOL_FLOW] [${client?.clientId}] Data validation passed - proceeding with evaluation`);
          
          // CRITICAL: Send tool response IMMEDIATELY before starting API call
          const proceedToolResponse = {
            functionResponses: [{
              response: { 
                output: { 
                  status: "proceeding",
                  action_taken: "started_api_call",
                  next_step: "wait_for_results",
                  message: "Evaluation API call initiated successfully"
                }
              },
              id: proceedFc.id,
              name: proceedFc.name,
            }]
          };
          
          console.log(`📤 [TOOL_FLOW] [${client?.clientId}] STEP 1: Sending tool response BEFORE starting API call`);
          console.log(`📤 [TOOL_FLOW] [${client?.clientId}] Tool response:`, JSON.stringify(proceedToolResponse, null, 2));
          
          client.sendToolResponse(proceedToolResponse);
          
          console.log(`✅ [TOOL_FLOW] [${client?.clientId}] STEP 2: Tool response sent to AI`);
          console.log(`🔄 [TOOL_FLOW] [${client?.clientId}] STEP 3: Starting property evaluation API call...`);
          
          // Clear any previous results and call evaluation directly (non-blocking)
          setEvaluationResult(null);
          handleProceedToEvaluation();
          
          console.log(`🔄 [TOOL_FLOW] [${client?.clientId}] STEP 4: handleProceedToEvaluation() called (async operation started)`);
          console.log(`⏳ [TOOL_FLOW] [${client?.clientId}] - Waiting for API response...`);
          console.log(`🚀 [TOOL_FLOW] [${client?.clientId}] ========================================`);
        } else {
          console.warn(`⚠️ [TOOL_FLOW] [${client?.clientId}] ========================================`);
          console.warn(`⚠️ [TOOL_FLOW] [${client?.clientId}] ERROR: No confirmed data available!`);
          console.warn(`⚠️ [TOOL_FLOW] [${client?.clientId}] - Ref value:`, currentConfirmedData);
          console.warn(`⚠️ [TOOL_FLOW] [${client?.clientId}] - This means confirm_evaluation_data was never called`);
          console.warn(`⚠️ [TOOL_FLOW] [${client?.clientId}] - AI must call confirm_evaluation_data BEFORE proceed_to_evaluation`);
          console.warn(`⚠️ [TOOL_FLOW] [${client?.clientId}] - Sending error response to AI...`);
          
          const errorResponse = {
            functionResponses: [{
              response: { 
                output: { 
                  status: "error",
                  error: "CRITICAL ERROR: No property data confirmed yet. You must call confirm_evaluation_data FIRST with all collected property information before calling proceed_to_evaluation.",
                  action_required: "call_confirm_evaluation_data_first",
                  missing_step: "Step 6 was skipped - you need to call confirm_evaluation_data to display the data panel before proceeding",
                  next_action: "Call confirm_evaluation_data with all collected property data immediately"
                }
              },
              id: proceedFc.id,
              name: proceedFc.name,
            }]
          };
          
          console.warn(`⚠️ [TOOL_FLOW] [${client?.clientId}] Error response:`, JSON.stringify(errorResponse, null, 2));
          client.sendToolResponse(errorResponse);
          console.warn(`⚠️ [TOOL_FLOW] [${client?.clientId}] Error response sent - AI should call confirm_evaluation_data`);
          console.warn(`⚠️ [TOOL_FLOW] [${client?.clientId}] ========================================`);
        }
      }
      
      // Send tool responses back
      if (evalResult) {
        console.log("📤 [GEMINI] Sending evaluation result back to Gemini");
        client.sendToolResponse({
          functionResponses: toolCall.functionCalls.map(fc => ({
            response: evalResult,
            id: fc.id
          }))
        });
      }
    };

    console.log(`🔔 [EVENT_LISTENER] Subscribing to toolcall events on client: ${client?.clientId}`);
    client.on("toolcall", onToolCall);

    // Add listener for AI text responses to debug what the AI is saying
    const onContent = (content: any) => {
      console.log(`💬 [AI_RESPONSE] ========================================`);
      console.log(`💬 [AI_RESPONSE] [${client?.clientId}] AI response received`);
      console.log(`💬 [AI_RESPONSE] [${client?.clientId}] Content type:`, content.modelTurn ? 'modelTurn' : 'other');
      
      if (content.modelTurn) {
        console.log(`💬 [AI_RESPONSE] [${client?.clientId}] Model turn has ${content.modelTurn.parts?.length || 0} parts`);
        content.modelTurn.parts?.forEach((part: any, index: number) => {
          if (part.text) {
            console.log(`💬 [AI_RESPONSE] [${client?.clientId}] 📝 AI TEXT (part ${index}):`);
            console.log(`💬 [AI_RESPONSE] [${client?.clientId}] "${part.text}"`);
          }
          if (part.inlineData) {
            console.log(`💬 [AI_RESPONSE] [${client?.clientId}] 🖼️ AI MEDIA (part ${index})`);
          }
        });
      } else {
        console.log(`💬 [AI_RESPONSE] [${client?.clientId}] Full content:`, content);
      }
      console.log(`💬 [AI_RESPONSE] [${client?.clientId}] ========================================`);
    };
    
    console.log(`🔔 [EVENT_LISTENER] Subscribing to content events on client: ${client?.clientId}`);
    client.on("content", onContent);

    return () => {
      console.log(`🔕 [EVENT_LISTENER] ========================================`);
      console.log(`🔕 [EVENT_LISTENER] Unsubscribing from events on client: ${client?.clientId}`);
      client.off("toolcall", onToolCall);
      client.off("content", onContent);
      console.log(`🔕 [EVENT_LISTENER] ========================================`);
    };
  }, [client, handleEvaluationSuccess, handleProceedToEvaluation]); // CRITICAL FIX: Removed confirmedData and showEvaluationButton - using refs instead

  // Check if we're in test mode
  const isTestMode = new URLSearchParams(window.location.search).get('testEvaluation') === 'true';
  const isPartialTestMode = new URLSearchParams(window.location.search).get('testPartialData') === 'true';

  return (
    <div className="flex flex-col w-full h-full gap-2">
      {/* Full test mode banner */}
      {isTestMode && confirmedData && showTestBanner && (
        <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 m-2">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🧪</span>
              <h3 className="text-lg font-bold text-yellow-800">TEST MODE ACTIVE (Complete Data)</h3>
            </div>
            <button
              onClick={() => setShowTestBanner(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: 'var(--Neutral-50, #6b7280)',
                padding: '4px 8px',
                borderRadius: '4px'
              }}
              title="Close banner"
            >
              ✕
            </button>
          </div>
          <p className="text-sm text-yellow-700 mb-3">
            Sample property data has been loaded. Click "Proceed to Evaluation" below to test the price prediction API.
          </p>
          <div className="bg-white rounded p-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div><strong>Property Type:</strong> {confirmedData.property_type}</div>
              <div><strong>Bedrooms:</strong> {confirmedData.bedrooms}</div>
              <div><strong>Bathrooms:</strong> {confirmedData.bathrooms}</div>
              <div><strong>Size:</strong> {confirmedData.size} sqm</div>
              <div><strong>Emirate:</strong> {confirmedData.emirate}</div>
              <div><strong>Community:</strong> {confirmedData.community}</div>
              <div><strong>Furnished:</strong> {confirmedData.furnished}</div>
              <div><strong>Building:</strong> {confirmedData.building_name_num}</div>
            </div>
          </div>
        </div>
      )}
      
      {/* Partial test mode banner */}
      {isPartialTestMode && confirmedData && showTestBanner && (
        <div className="bg-orange-50 border-2 border-orange-400 rounded-lg p-4 m-2">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🔍</span>
              <h3 className="text-lg font-bold text-orange-800">PARTIAL TEST MODE ACTIVE</h3>
            </div>
            <button
              onClick={() => setShowTestBanner(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: 'var(--Neutral-50, #6b7280)',
                padding: '4px 8px',
                borderRadius: '4px'
              }}
              title="Close banner (normal flow will continue)"
            >
              ✕
            </button>
          </div>
          <p className="text-sm text-orange-700 mb-3">
            Simulating state after camera analysis. Some fields are missing - AI should ask for them.
          </p>
          
          {/* Collected Data */}
          <div className="bg-white rounded p-3 text-sm mb-3">
            <p className="font-bold text-green-700 mb-2">✅ Data Collected from Camera:</p>
            <div className="grid grid-cols-2 gap-2">
              <div><strong>Property Type:</strong> {confirmedData.property_type}</div>
              <div><strong>Bedrooms:</strong> {confirmedData.bedrooms}</div>
              <div><strong>Bathrooms:</strong> {confirmedData.bathrooms}</div>
              <div><strong>Size:</strong> {confirmedData.size} sqm</div>
              <div><strong>Furnished:</strong> {confirmedData.furnished}</div>
            </div>
          </div>
          
          {/* Missing Data */}
          <div className="bg-white rounded p-3 text-sm">
            <p className="font-bold text-red-700 mb-2">❌ Missing Information (AI should ask):</p>
            <ul className="list-disc list-inside text-red-600 space-y-1">
              <li>Emirate (required)</li>
              <li>Community (required)</li>
              <li>Street address (required)</li>
              <li>Building name/number (required)</li>
              <li>Customer name (required)</li>
            </ul>
          </div>
          
          <p className="text-xs text-orange-600 mt-3 italic">
            💡 Click X to close this banner. The AI conversation flow will continue normally.
          </p>
        </div>
      )}
      {jsonString && (
        <div className="flex w-full flex-col">
          <Altair spec={jsonString} />
        </div>
      )}
      
      {/* Confirmation Panel - shows when data is confirmed but not yet evaluated */}
      {/* Shows in normal flow when confirm_evaluation_data is called */}
      {confirmedData && !evaluationResult && !showEvaluationButton && !isTestMode && !isPartialTestMode && !isEvaluating && (
        <div style={{
          backgroundColor: 'var(--Neutral-00, #ffffff)',
          borderRadius: '12px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          padding: '24px',
          marginTop: '16px',
          marginLeft: '8px',
          marginRight: '8px',
          border: '2px solid var(--Blue-500, #3b82f6)',
          maxWidth: '800px',
          margin: '16px auto'
        }}>
          <h3 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: 'var(--Blue-800, #1e40af)',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            📋 Details for Evaluation
          </h3>
          <div style={{
            backgroundColor: 'var(--Neutral-5, #f9fafb)',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid var(--Neutral-15, #e5e7eb)',
            marginBottom: '12px'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '12px',
              fontSize: '14px',
              color: 'var(--Neutral-80, #374151)'
            }}>
              <div><strong>Property Type:</strong> {confirmedData.property_type || 'N/A'}</div>
              <div><strong>Bedrooms:</strong> {confirmedData.bedrooms || 'N/A'}</div>
              <div><strong>Bathrooms:</strong> {confirmedData.bathrooms || 'N/A'}</div>
              <div><strong>Size:</strong> {confirmedData.size ? `${confirmedData.size} sqm` : 'N/A'}</div>
              <div><strong>Emirate:</strong> {confirmedData.emirate || 'N/A'}</div>
              <div><strong>Community:</strong> {confirmedData.community || 'N/A'}</div>
              <div><strong>Street:</strong> {confirmedData.street || 'N/A'}</div>
              <div><strong>Building:</strong> {confirmedData.building_name_num || 'N/A'}</div>
              <div><strong>Customer:</strong> {confirmedData.customer_name || 'N/A'}</div>
              <div><strong>Furnished:</strong> {confirmedData.furnished || 'N/A'}</div>
            </div>
          </div>
          
          {/* Validation Warnings */}
          {(confirmedData.validation_warning || confirmedData.property_type_warning || confirmedData.emirate_warning) && (
            <div style={{
              padding: '12px',
              backgroundColor: 'var(--Red-50, #fef2f2)',
              borderRadius: '8px',
              border: '1px solid var(--Red-500, #ef4444)',
              marginBottom: '12px'
            }}>
              {confirmedData.validation_warning && (
                <p style={{
                  margin: '0 0 8px 0',
                  color: 'var(--Red-700, #b91c1c)',
                  fontSize: '14px'
                }}>
                  ⚠️ {confirmedData.validation_warning}
                </p>
              )}
              {confirmedData.property_type_warning && (
                <p style={{
                  margin: '0 0 8px 0',
                  color: 'var(--Red-700, #b91c1c)',
                  fontSize: '14px'
                }}>
                  ⚠️ {confirmedData.property_type_warning}
                </p>
              )}
              {confirmedData.emirate_warning && (
                <p style={{
                  margin: '0',
                  color: 'var(--Red-700, #b91c1c)',
                  fontSize: '14px'
                }}>
                  ⚠️ {confirmedData.emirate_warning}
                </p>
              )}
            </div>
          )}
          
          <p style={{
            margin: '0',
            fontSize: '14px',
            color: 'var(--Neutral-60, #4b5563)',
            fontStyle: 'italic'
          }}>
            💬 Please say "proceed with evaluation" to the AI
          </p>
        </div>
      )}
      
      {evaluationResult && (
        <EvaluationResults
          result={evaluationResult}
          confirmedData={confirmedData}
          onClose={handleCloseResults}
        />
      )}
      {showEvaluationButton && confirmedData && !evaluationResult && (
        <div className="flex justify-center p-4">
          <button
            onClick={handleProceedToEvaluation}
            disabled={isEvaluating}
            className={`px-6 py-3 rounded-lg transition-colors font-semibold shadow-lg ${
              isEvaluating 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isEvaluating ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Evaluating...
              </span>
            ) : (
              'Proceed to Evaluation'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default memo(AltairComponent);
